// The Pulse metric trend readout (docs/phase-0/slice6-component-designs.md §4.1
// `.metric-trend`; UX §4.1/§4.6). The plain text readout ("{delta} vs {priorDate}") is
// ALWAYS the accessible content when trend data exists; an optional inline sparkline is a
// pure decorative enhancement beside it, never the sole carrier of the trend
// (`chart-type`/`direct-labeling`/`screen-reader-summary` per `ui-ux-pro-max`; UX §4.6).
// `priorDate` renders through the locale (`Date.prototype.toLocaleDateString`, matching
// every other date render in this codebase, e.g. `CredentialCard.tsx:50`).
//
// STRUCTURALLY UNFED IN THIS BUILD: `PulseMetricPoint` (pulse.ts:45-50) carries only the
// LATEST reading per metric - `pulse.int.test.ts:91` asserts exactly one point per metric
// - so no prior value/delta is ever computable from the landed wire. This component is
// built and directly unit-tested (`PulseMetricCard.test.tsx`) ready for a historical
// series to land; `PulseMetricCard` never currently supplies a `trend` prop with data.
import { pulseCopy } from "../copy/pulse.js";

export interface PulseTrendData {
  /** A pre-formatted delta string (e.g. "+12%", "-3") - this component never computes or
   * invents one. */
  readonly delta: string;
  /** An ISO-8601 timestamp for the prior reading being compared against. */
  readonly priorDate: string;
  /** Normalized 0-1 y-values, oldest first - decorative only, never the sole carrier of
   * the trend (UX §4.6). Omit to render text-only. */
  readonly sparkline?: readonly number[];
}

const SPARKLINE_WIDTH = 48;
const SPARKLINE_HEIGHT = 16;

const PulseTrendSparkline = ({
  points,
}: {
  readonly points: readonly number[];
}): React.JSX.Element | null => {
  if (points.length < 2) return null;
  const step = SPARKLINE_WIDTH / (points.length - 1);
  const path = points
    .map((p, i) => {
      const x = (i * step).toFixed(1);
      const y = (SPARKLINE_HEIGHT - Math.min(1, Math.max(0, p)) * SPARKLINE_HEIGHT).toFixed(1);
      return `${i === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");
  return (
    <svg
      width={SPARKLINE_WIDTH}
      height={SPARKLINE_HEIGHT}
      viewBox={`0 0 ${String(SPARKLINE_WIDTH)} ${String(SPARKLINE_HEIGHT)}`}
      aria-hidden="true"
      focusable="false"
    >
      <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  );
};

export const PulseMetricTrend = ({
  trend,
}: {
  readonly trend: PulseTrendData;
}): React.JSX.Element => (
  <p className="metric-trend">
    <span>
      {pulseCopy("pulse.metric.trend", {
        delta: trend.delta,
        priorDate: new Date(trend.priorDate).toLocaleDateString(),
      })}
    </span>
    {trend.sparkline !== undefined ? <PulseTrendSparkline points={trend.sparkline} /> : null}
  </p>
);
